import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, MessageReaction, ThreadMessage } from "@rakazo/contracts";
import { canReactToThreadMessage, MESSAGE_REACTIONS } from "@rakazo/contracts";
import { canMarkMessageUnread } from "@rakazo/core";
import {
  BotAvatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@rakazo/ui-web";
import { Copy, ListTodo, MessageSquareDot, MoreHorizontal, Reply, Smile } from "lucide-react";
import { useState } from "react";
import { MessageHoverMetadata } from "../../components/MessageHoverMetadata";
import { copyableMessageText } from "../../lib/message-text";
import { FALLBACK_BOT_COLOR } from "./chat-shared";

export type MessageAskBot = Pick<Bot, "id" | "name" | "color" | "avatarSet">;

/** Hands a message to the rest of the team; each part is absent where it does not apply. */
export type MessageTeamActions = {
  /** The bot a task made from this message is assigned to, or undefined when none can be. */
  taskAssignee: (message: ThreadMessage) => string | undefined;
  makeTask: (message: ThreadMessage, assigneeBotId: string) => void;
  askBots: (message: ThreadMessage) => readonly MessageAskBot[];
  ask: (message: ThreadMessage, bot: MessageAskBot) => void;
};

export function MessageHoverActions({
  message,
  side,
  onReply,
  onMarkUnread,
  markedUnread = false,
  onReact,
  team,
}: {
  message: ThreadMessage;
  side: "start" | "end";
  onReply: (message: ThreadMessage) => void;
  onMarkUnread: (message: ThreadMessage) => void;
  markedUnread?: boolean;
  onReact: (message: ThreadMessage, reaction: MessageReaction) => Promise<void>;
  team?: MessageTeamActions;
}) {
  const { t } = useLingui();
  const [moreOpen, setMoreOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);

  // Streaming progress bubbles keep hover free for selection / stop clicks.
  if (message.id.startsWith("progress:")) return null;

  // Read only while the menu is open, so a long transcript does not filter the team per row.
  const taskAssignee = moreOpen ? team?.taskAssignee(message) : undefined;
  const askBots = moreOpen ? (team?.askBots(message) ?? []) : [];

  function copyMessage() {
    const text = copyableMessageText(message);
    if (!text || !navigator.clipboard) return;
    void navigator.clipboard.writeText(text).catch(() => undefined);
  }

  const iconButtonClass =
    "grid h-11 w-11 place-items-center text-muted-foreground transition-colors hover:text-foreground [@media(hover:hover)_and_(pointer:fine)]:h-7 [@media(hover:hover)_and_(pointer:fine)]:w-7";

  return (
    <MessageHoverMetadata pinned={moreOpen || reactionsOpen} side={side}>
      <div data-testid="message-hover-actions" className="flex items-center gap-0.5">
        {canReactToThreadMessage(message) ? (
          <Popover open={reactionsOpen} onOpenChange={setReactionsOpen}>
            <PopoverTrigger aria-label={t`React`} className={iconButtonClass}>
              <Smile size={15} strokeWidth={1.7} />
            </PopoverTrigger>
            <PopoverContent
              align={side === "end" ? "start" : "end"}
              className="w-auto flex-row gap-0 rounded-2xl p-1.5"
              aria-label={t`Reactions`}
            >
              {MESSAGE_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  aria-label={emoji}
                  className="grid h-11 w-11 place-items-center rounded-xl text-2xl hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => {
                    setReactionsOpen(false);
                    void onReact(message, emoji);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        ) : null}
        <button
          type="button"
          aria-label={t`Reply`}
          onClick={() => onReply(message)}
          className={iconButtonClass}
        >
          <Reply size={15} strokeWidth={1.7} />
        </button>
        <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
          <DropdownMenuTrigger aria-label={t`More`} className={iconButtonClass}>
            <MoreHorizontal size={15} strokeWidth={1.7} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align={side === "end" ? "start" : "end"} className="w-auto">
            <DropdownMenuItem
              onClick={() => {
                setMoreOpen(false);
                onReply(message);
              }}
            >
              <Reply size={14} strokeWidth={1.7} />
              <Trans>Reply</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={copyMessage}>
              <Copy size={14} strokeWidth={1.7} />
              <Trans>Copy</Trans>
            </DropdownMenuItem>
            {taskAssignee ? (
              <DropdownMenuItem
                onClick={() => {
                  setMoreOpen(false);
                  team?.makeTask(message, taskAssignee);
                }}
              >
                <ListTodo size={14} strokeWidth={1.7} />
                <Trans>Make a task</Trans>
              </DropdownMenuItem>
            ) : null}
            {askBots.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                {/* A big team scrolls here so Mark as unread stays in reach. */}
                <DropdownMenuGroup className="max-h-60 overflow-y-auto">
                  {askBots.map((bot) => {
                    const name = bot.name;
                    return (
                      <DropdownMenuItem
                        key={bot.id}
                        onClick={() => {
                          setMoreOpen(false);
                          team?.ask(message, bot);
                        }}
                      >
                        <BotAvatar
                          avatarSet={bot.avatarSet}
                          color={bot.color || FALLBACK_BOT_COLOR}
                          identity={bot.id}
                          size={14}
                        />
                        <span dir="auto">
                          <Trans>Ask {name}</Trans>
                        </span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuGroup>
              </>
            ) : null}
            {canMarkMessageUnread(message) && !markedUnread ? (
              <>
                {askBots.length > 0 ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem
                  onClick={() => {
                    setMoreOpen(false);
                    onMarkUnread(message);
                  }}
                >
                  <MessageSquareDot size={14} strokeWidth={1.7} />
                  <Trans>Mark as unread</Trans>
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </MessageHoverMetadata>
  );
}
